'use client'

import { startAuthentication, startRegistration } from '@simplewebauthn/browser'

export const getURLSearchParams = (): URLSearchParams | undefined => {
    if (typeof window === 'undefined') {
        return undefined
    }
    return new URLSearchParams(window.location.search)
}

export const getApiUrlPrefix = (): string => {
    if (typeof window === 'undefined') {
        return process.env['API_URL_PREFIX']!
    }
    let apiUrlPrefix: string
    if (window.location.host.includes('circuitly.com')) {
        apiUrlPrefix = `${window.location.protocol}//api.${window.location.host}`
    } else {
        apiUrlPrefix = `${window.location.protocol}//${window.location.host}`
    }
    return apiUrlPrefix
}

export const getSiteUrlPrefix = (): string => {
    if (typeof window === 'undefined') {
        return process.env['SITE_URL_PREFIX']!
    }
    return `${window.location.protocol}//${window.location.host}`
}

export const waitlistOnly = (): boolean => {
    return getSiteUrlPrefix()?.startsWith('https://circuitly.com')
}

export const getSessionCookieName = (): string => {
    if (typeof window === 'undefined') {
        return process.env['ANALYTICS_COOKIE_NAME']!
    }
    let sessionCookieName: string
    if (window.location.host.includes('staging.circuitly.com')) {
        sessionCookieName = `X-Session-Staging-Token`
    } else if (window.location.host.includes('circuitly.com')) {
        sessionCookieName = `X-Session-Token`
    } else {
        const port = window.location.port
        if (port !== '' && port !== '9000') {
            sessionCookieName = `X-Session-Local-Token-${port}`
        } else {
            sessionCookieName = `X-Session-Local-Token`
        }
    }
    return sessionCookieName
}

export const getAnalyticsCookieName = (): string => {
    if (typeof window === 'undefined') {
        return process.env['ANALYTICS_COOKIE_NAME']!
    }
    let analyticsCookieName: string
    if (window.location.host.includes('staging.circuitly.com')) {
        analyticsCookieName = `X-Analytics-Staging-Token`
    } else if (window.location.host.includes('circuitly.com')) {
        analyticsCookieName = `X-Analytics-Token`
    } else {
        analyticsCookieName = `X-Analytics-Local-Token`
    }
    return analyticsCookieName
}

export const getSessionToken = (): string | undefined => {
    const sessionCookieName = getSessionCookieName()
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
        const [name, value] = cookie.split('=')
        if (name.trim() === sessionCookieName) {
            return value.trim()
        }
    }
    return undefined
}

export const getSignInDestination = (): string => {
    return getSessionToken() !== undefined ? '/conversation/new' : '/signin'
}

export const isPasskeySupported = (): boolean =>
    typeof window !== 'undefined' && window.PublicKeyCredential !== undefined && window.isSecureContext

const passkeyRequest = async <T>(path: string, body: unknown): Promise<T> => {
    const response = await fetch(`${getApiUrlPrefix()}${path}`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    })
    if (!response.ok) {
        const error = response.headers.get('content-type')?.startsWith('application/json')
            ? await response.json()
            : { message: await response.text() }
        throw new Error(error.message ?? 'Passkey request failed')
    }
    return response.json()
}

export const signupWithPasskey = async (
    fields: { firstName: string; lastName: string; emailAddress: string },
    options?: { returnTo?: string },
): Promise<{ redirectUrl?: string }> => {
    const suffix = options?.returnTo ? `?return_to=${encodeURIComponent(options.returnTo)}` : ''
    const begin = await passkeyRequest<{
        ceremonyId: string
        options: Parameters<typeof startRegistration>[0]['optionsJSON']
    }>('/v1/user/passkey/signup/options', fields)
    const response = await startRegistration({ optionsJSON: begin.options })
    return passkeyRequest(`/v1/user/passkey/signup/verify${suffix}`, {
        ceremonyId: begin.ceremonyId,
        response,
    })
}

export const signinWithPasskey = async (options?: {
    returnTo?: string
    conditional?: boolean
}): Promise<{ redirectUrl?: string }> => {
    const suffix = options?.returnTo ? `?return_to=${encodeURIComponent(options.returnTo)}` : ''
    const begin = await passkeyRequest<{
        ceremonyId: string
        options: Parameters<typeof startAuthentication>[0]['optionsJSON']
    }>('/v1/user/passkey/signin/options', {})
    const response = await startAuthentication({
        optionsJSON: begin.options,
        useBrowserAutofill: options?.conditional ?? false,
    })
    return passkeyRequest(`/v1/user/passkey/signin/verify${suffix}`, {
        ceremonyId: begin.ceremonyId,
        response,
    })
}

export const getAnalyticsToken = (): string | undefined => {
    const analyticsToken = getAnalyticsCookieName()
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
        const [name, value] = cookie.split('=')
        if (name.trim() === analyticsToken) {
            return value.trim()
        }
    }
    return undefined
}

let distinctId: string | undefined = undefined
export const track = async (eventName: string, eventArgs?: { [key: string]: any }): Promise<void> => {
    await fetch(`${getApiUrlPrefix()}/v1/track`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            distinctId: getAnalyticsToken() ?? distinctId,
            currentUrl: window.location.href,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            eventName,
            eventArgs,
        }),
    })
        .then(async response => {
            distinctId = (await response.json()).distinctId
        })
        .catch(err => {
            // eat this error
            //  we don't want to hard crash in the case that mixpanel is down
        })
}

export const signup = async (
    userFields: {
        firstName: string
        lastName: string
        emailAddress: string
        password: string
    },
    options?: {
        referredBy?: string
        referredByOther?: string
        subscribeToNewsletter?: boolean
        returnTo?: string
    },
): Promise<{ redirectUrl?: string }> => {
    let url = `${getApiUrlPrefix()}/v1/user/signup`
    if (options?.returnTo) {
        url += `?return_to=${options.returnTo}`
    }
    let response: Response
    try {
        response = await fetch(url, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...userFields,
                referredBy: options?.referredBy,
                referredByOther: options?.referredByOther,
                subscribeToNewsletter: options?.subscribeToNewsletter,
            }),
        })
    } catch (err) {
        throw new Error(`failed to sign up`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    }
    return response.json()
}

export type OAuthKinds = 'google' | 'gitlab' | 'github'

export const OAuthScopes: Record<OAuthKinds, string[]> = {
    google: ['openid', 'profile', 'email'],
    github: ['user:email'],
    gitlab: ['read_user', 'email'],
}

export const authorize = async (
    kind: OAuthKinds,
    scopes: string[],
    options?: {
        returnTo?: string
        createAccountIfNotExists?: boolean
        reason?: 'signin' | 'waitlist'
    },
): Promise<{ redirectUrl?: string }> => {
    let response: Response
    try {
        response = await fetch(`${getApiUrlPrefix()}/v1/authorize`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                kind,
                scopes,
                internalState: {
                    reason: options?.reason ?? 'signin',
                    redirect: options?.returnTo,
                    createAccountIfNotExists: options?.createAccountIfNotExists,
                },
            }),
        })
    } catch (err) {
        throw new Error(`failed to authorize with '${kind}'`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            if (error.reason === 'AuthorizedScopesExists') {
                return { redirectUrl: undefined }
            } else {
                throw new Error(error.message)
            }
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    } else {
        const { authorizationUrl } = await response.json()
        return { redirectUrl: authorizationUrl }
    }
}

export const signin = async (
    emailAddress: string,
    password: string,
    options?: { returnTo?: string },
): Promise<{ userId: string; redirectUrl?: string }> => {
    let url = `${getApiUrlPrefix()}/v1/user/signin`
    if (options?.returnTo) {
        url += `?return_to=${options.returnTo}`
    }
    let response: Response
    try {
        response = await fetch(url, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                emailAddress,
                password,
            }),
        })
    } catch (err) {
        throw new Error(`failed to sign in`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    } else {
        const result = await response.json()
        return result
    }
}

export const forgotPassword = async (emailAddress: string): Promise<void> => {
    let response: Response
    try {
        response = await fetch(`${getApiUrlPrefix()}/v1/user/forgotpassword`, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                emailAddress,
            }),
        })
    } catch (err) {
        throw new Error(`failed to send forgot password request`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    }
}

export const resetPassword = async (token: string, password: string): Promise<{ redirectUrl?: string }> => {
    let response: Response
    try {
        response = await fetch(`${getApiUrlPrefix()}/v1/user/resetpassword`, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                password,
                token,
            }),
        })
    } catch (err) {
        throw new Error(`failed to reset password`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    } else {
        const result = await response.json()
        return result
    }
}

export const updateUser = async ({
    metadata,
    subscribeToNewsletter,
}: {
    subscribeToNewsletter?: boolean
    metadata?: {
        role?: string
        otherRole?: string
        primaryObjective?: string
        otherPrimaryObjective?: string
        edaTool?: string
        otherEdaTool?: string
    }
}): Promise<void> => {
    let response: Response
    try {
        response = await fetch(`${getApiUrlPrefix()}/v1/user`, {
            method: 'PATCH',
            mode: 'cors',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                metadata,
                subscribeToNewsletter,
            }),
        })
    } catch (err) {
        throw new Error(`failed to update user`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    }
}

export type WaitlistAreaOfInterest =
    | 'WAITLIST_AREA_OF_INTEREST_JOIN_WAITLIST'
    | 'WAITLIST_AREA_OF_INTEREST_REQUEST_DEMO'
    | 'WAITLIST_AREA_OF_INTEREST_AI_STRATEGY_SESSION'

export interface IAddToWaitlistOptions {
    email: string
    fullName: string
    areaOfInterest: WaitlistAreaOfInterest
    note?: string
}

export const addToWaitlist = async ({
    email,
    fullName,
    areaOfInterest,
    note,
}: IAddToWaitlistOptions): Promise<void> => {
    let response: Response
    try {
        response = await fetch(`${getApiUrlPrefix()}/v1/waitlist`, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email,
                fullName,
                areaOfInterest,
                note,
            }),
        })
    } catch (err) {
        throw new Error(`failed to join the waitlist`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    }
}

export interface IPlanMetadata {
    default?: boolean
    order?: number
    forever?: boolean
    commercial?: boolean
    academic?: boolean
    fullPrice?: IPricePoint[]
}

export interface IFlags {
    [key: string]: boolean | number | string
}

export const getFlags = async (): Promise<IFlags> => {
    const response = await fetch(`${getApiUrlPrefix()}/v1/flags`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    })
    if (!response.ok) {
        throw new Error(await response.text())
    }
    return response.json()
}

export interface IEntitlement {
    featureId: string
    level?: number
}

export interface IPricePeriod {
    unit: string
    value: number
}

export interface IPricePoint {
    id: string
    currencyCode: string
    price: number
    period: IPricePeriod
    trialPeriod?: IPricePeriod
    creditsByType: Record<string, number>
}

export interface ICreditAddon {
    id: string
    title: string
    creditsByType: Record<string, number>
    pricePoints: IPricePoint[]
}

export interface IPlan {
    id: string
    title: string
    description: string
    metadata: IPlanMetadata | null
    disabled: boolean
    entitlements: IEntitlement[]
    pricePoints: IPricePoint[]
}

export interface ILevel {
    title: string
    value: number
}

export interface IFeature {
    id: string
    title: string
    description: string
    levels?: ILevel[]
    order?: number
}

export const listPlans = async (): Promise<{
    site: string
    plans: IPlan[]
    features: { [id: string]: IFeature }
    creditAddons: ICreditAddon[]
}> => {
    let response: Response
    try {
        response = await fetch(`${getApiUrlPrefix()}/v1/plans`, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
    } catch (err) {
        throw new Error(`failed to list plans`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    } else {
        const result = await response.json()
        return result
    }
}

export const checkout = async (pricePointId: string, addonPricePointIds: string[]): Promise<any> => {
    let response: Response
    try {
        response = await fetch(`${getApiUrlPrefix()}/v1/checkout`, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pricePointId,
                addonPricePointIds,
            }),
        })
    } catch (err) {
        throw new Error(`failed to create checkout`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    }
    const result = await response.json()
    return result.hostedPage
}

export const refreshUserPlan = async (): Promise<any> => {
    let response: Response
    try {
        response = await fetch(`${getApiUrlPrefix()}/v1/refreshPlan`, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
        })
    } catch (err) {
        throw new Error(`failed to refresh user plan`)
    }
    if (!response.ok) {
        if (response?.headers?.get('content-type')?.startsWith('application/json')) {
            const error = await response.json()
            throw new Error(error.message)
        } else {
            const message = await response.text()
            throw new Error(message)
        }
    }
}
